import { Alert, Button, Flex, Steps, Title } from "@/atoms"
import { Trans } from "@/hooks/useTranslation"
import { ExternalLinkOutlined, InfoCircleOutlined } from "@/icons"

import styles from "./InstallStepsSection.module.scss"

import type { InstallStep, SectionInfo } from "@/api/useIntegrations.types"

interface InstallStepsSectionProps {
  section: SectionInfo
  onButtonClick: () => void
}

export const InstallStepsSection = ({
  section,
  onButtonClick
}: InstallStepsSectionProps) => (
  <div className={styles.container}>
    <Title level={5} className={styles.title}>
      {section.title}
    </Title>
    {section.installSteps && (
      <Steps
        size="small"
        theme="dark"
        className={styles.steps}
        orientation="vertical"
        items={section.installSteps.map(buildStepItem(onButtonClick))}
      />
    )}
    {section.importantNote && (
      <Alert
        className={styles.importantAlert}
        title={<Trans>{section.importantNote}</Trans>}
        type="warning"
        showIcon={false}
      />
    )}
  </div>
)

const buildStepItem = (onButtonClick: () => void) => (step: InstallStep) => ({
  title: step.title,
  status: "process" as const,
  description: buildStepDescription(step, onButtonClick)
})

const buildStepDescription = (step: InstallStep, onButtonClick: () => void) => {
  const { button, description, subSteps, anchorId } = step

  if (!button && !description && !subSteps) return undefined

  return (
    <div id={anchorId} className={styles.stepContent}>
      {button && (
        <Flex gap={14} align="center">
          <Button
            icon={<ExternalLinkOutlined />}
            type="primary"
            href={button.link}
            target="_blank"
            onClick={onButtonClick}
          >
            {button.label}
          </Button>
          {button.alert && (
            <Alert
              showIcon
              icon={<InfoCircleOutlined />}
              className={styles.buttonAlert}
              title={
                <span data-anchor={button.anchorId || ""}>
                  <Trans>{button.alert}</Trans>
                </span>
              }
              type="warning"
            />
          )}
        </Flex>
      )}
      {description && <span>{description}</span>}
      {subSteps && (
        <ul className={styles.subStepsList}>
          {subSteps.map((subStep, index) => (
            <li key={index}>{subStep}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
